import React, { useEffect, useState } from "react";
import PrimaryButton from "../../PrimaryButton";
import GreyButton from "../../GreyButton";
import Confetti from "../../Confetti/Confetti";
import MobileViews from "./MobileViews";
import {
  approveOrRejectResources,
  createOffer,
  getNearestOffersByMerchant,
  getOfferById,
  getOffersByMerchant,
} from "../../../../services/offerService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getLocationById } from "../../../../services/franchiseService";
import toast from "../../../../utils/toast";
import { useOfferFormContext } from "../OfferFormContext";

const OfferReview = ({
  handlePrevious,
  editOfferData,
  setPayloadData,
  selectedMerchant,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const offerId = useSelector(
    (state) => state?.offerReducer?.createdOffer?._id
  );
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const createdOfferFromRedux = useSelector(
    (state) => state?.offerReducer?.createdOffer
  );

  const [offerData, setOfferData] = useState(null);
  const [firstLocation, setFirstLocation] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // removed duplicates
  const [isFirstOffer, setIsFirstOffer] = useState(false);

  const {
    setNextLabel,
    registerNextAction,
    setIsNextLoading,
    setIsNextDisabled
  } = useOfferFormContext();

  useEffect(() => {
    setNextLabel("Submit");
    return () => setNextLabel("Next");
  }, [setNextLabel]);

  const getOfferData = async () => {
    if (createdOfferFromRedux && createdOfferFromRedux._id === offerId) {
      setOfferData(createdOfferFromRedux);
      return;
    }

    const res = await dispatch(getOfferById({ id: offerId }))?.unwrap();
    if (res?.success) {
      setOfferData({
        ...res.data,
        offerCodes:
          createdOfferFromRedux?.offerCodes || res.data?.offerCodes || [],
      });
    }
  };

  const getLocationData = async () => {
    const locId = offerData?.tempLocations?.[0]?._id;
    if (!locId) return;

    const res = await dispatch(getLocationById({ id: locId }))?.unwrap();
    if (res?.success) {
      setFirstLocation(res.data);
    }
  };

  const getMerchantData = async () => {
    const payload = {
      lat: firstLocation?.googlePlaceId?.lat,
      lng: firstLocation?.googlePlaceId?.lng,
      merchantId: offerData?.merchant?._id,
    };
    const res = await dispatch(getNearestOffersByMerchant(payload))?.unwrap();
    if (res?.success) {
      setMerchantData(res.data);
    }
  };

  // Check if this is the merchant's first offer
  const checkIfFirstOffer = async () => {
    // Don't check for first offer if editing an existing offer
    if (editOfferData) {
      setIsFirstOffer(false);
      return;
    }

    // Get the merchant ID - for admin, use selectedMerchant; for merchant, use offerData merchant
    const merchantId = selectedMerchant?._id || offerData?.merchant?._id;

    if (!merchantId) {
      setIsFirstOffer(false);
      return;
    }

    try {
      // Fetch merchant's offers to check if they have any completed offers
      const res = await dispatch(
        getOffersByMerchant({
          merchantId: merchantId,
          limit: 100, // Get a reasonable number to check
          page: 1,
        })
      )?.unwrap();

      if (res?.success) {
        // Filter for completed offers (isCreated: true) excluding the current offer
        const existingOffers = res?.data || [];
        const completedOffers = existingOffers.filter(
          (offer) => offer?.isCreated === true && offer?._id !== offerData?._id
        );

        // This is the first offer if there are no existing completed offers
        setIsFirstOffer(completedOffers.length === 0);
      } else {
        setIsFirstOffer(false);
      }
    } catch (error) {
      // If error occurs, default to not showing celebration
      console.warn("Error checking if first offer:", error);
      setIsFirstOffer(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) await getOfferData();
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      createdOfferFromRedux?.offerCodes &&
      offerData &&
      createdOfferFromRedux._id === offerData._id
    ) {
      setOfferData((prev) => ({
        ...prev,
        offerCodes: createdOfferFromRedux.offerCodes,
      }));
    }
  }, [createdOfferFromRedux?.offerCodes]);

  useEffect(() => {
    if (offerData) {
      getLocationData();
    }
  }, [offerData]);

  useEffect(() => {
    if (offerData && firstLocation) {
      getMerchantData();
    }
  }, [firstLocation]);

  // Check if this is the first offer when offerData is available
  useEffect(() => {
    if (offerData && !editOfferData && role !== "admin") {
      checkIfFirstOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData, editOfferData, role]);

  useEffect(() => {
    if (!offerData) return;
    const payload = {
      category: offerData?.category?._id,
      subCategory: offerData?.subCategory?.map((s) => s?._id),
      title: offerData?.title,
      offerType: offerData?.offerType,
      discountRange: offerData?.discountRange,
      moneySaved: offerData?.moneySaved,
      thumbnailImage: offerData?.thumbnailImage,
      previewImages: offerData?.previewImages,
      description: offerData?.description,
      redemptionDays: offerData?.redemptionDays,
      startDate: offerData?.startDate,
      endDate: offerData?.endDate,
      ruleAndRegulation: offerData?.ruleAndRegulation?.map((r) => r?._id),
      locations: offerData?.tempLocations?.map((l) => l?._id),
      discountTitle: offerData?.discountTitle,
      id: offerData?._id,
      isCreated: false,
    };
    setPayloadData(payload);
  }, [offerData]);

  const handleSubmit = async () => {
    const finalOfferCodes =
      createdOfferFromRedux?.offerCodes || offerData?.offerCodes;

    const resolvedOnlineOption =
      typeof offerData?.onlineOption === "string"
        ? offerData?.onlineOption
        : (Array.isArray(finalOfferCodes) && finalOfferCodes.length > 0)
          ? "ONLINE_WITH_CODE"
          : "ONLINE";

    const payload = {
      merchantId: selectedMerchant?._id,
      category: offerData?.category?._id,
      subCategory: offerData?.subCategory?.map((s) => s?._id),
      title: offerData?.title,
      offerType: offerData?.offerType,
      offerDiscountType: offerData?.offerDiscountType,
      offerCodes: finalOfferCodes,
      websiteURL: offerData?.websiteURL,
      onlineOption: resolvedOnlineOption,
      necessaryRule: offerData?.necessaryRule,
      ...(offerData?.redemptionFrequency
        ? { redemptionFrequency: offerData?.redemptionFrequency }
        : {}),
      redemptionDays: offerData?.redemptionDays,
      availableAcrossAllStores: offerData?.availableAcrossAllStores,
      discountRange: offerData?.discountRange,
      fixedDollarAmount: offerData?.fixedDollarAmount,
      thumbnailImage: offerData?.thumbnailImage,
      previewImages: offerData?.previewImages,
      ...(offerData?.description?.trim()
        ? { description: offerData?.description?.trim() }
        : {}),
      redemptionMethod: offerData?.redemptionMethod,
      startTime: offerData?.startTime,
      endTime: offerData?.endTime,
      callback_url: offerData?.callback_url,
      ruleAndRegulation: offerData?.ruleAndRegulation?.map((r) => r?._id),
      locations: offerData?.tempLocations?.map((l) => l?._id),
      discountTitle: offerData?.discountTitle,
      startDate: offerData?.startDate,
      endDate: offerData?.endDate,
      id: offerData?._id,
      isCreated: true,
    };

    if (!offerData?.description || `${offerData?.description}`.trim().length === 0) {
      delete payload.description;
    }

    try {
      const createRes = await dispatch(
        createOffer({
          ...payload,
          offerId: editOfferData ? editOfferData._id : offerData._id,
        })
      )?.unwrap();

      if (!createRes?.success) return;

      if (role === "admin") {
        const statusRes = await dispatch(
          approveOrRejectResources({
            type: "offer",
            status: "approved",
            ids: [offerData?._id],
            merchantId: selectedMerchant?._id,
          })
        )?.unwrap();
        if (statusRes?.success) navigate("/offer-list");
        return;
      }

      // Always show the success modal
      setShowSuccessModal(true);
      // Only show celebration (confetti) if this is the first offer
      if (isFirstOffer) {
        setShowCelebration(true);
      } else {
        setShowCelebration(false);
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        "We couldn't submit your offer just yet. Please review the required fields and try again.";
      toast.error(message);
    }
  };

  useEffect(() => {
    let timer;
    if (showCelebration) {
      timer = setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showCelebration]);

  const handleCloseCelebration = () => {
    setShowCelebration(false);
    setShowSuccessModal(false);
    navigate("/merchant/offer-list");
  };

  // Register the Next (now Submit) action
  useEffect(() => {
    registerNextAction(() => handleSubmit());
  }, [registerNextAction, offerData, editOfferData, selectedMerchant, role, isFirstOffer]); // Dependencies crucial for handleSubmit closure

  // Sync loading state
  useEffect(() => {
    setIsNextLoading(loading);
  }, [loading, setIsNextLoading]);

  const isSubmitBusy = Boolean(loading);
  const defaultLabel = editOfferData ? "Resubmit" : "Submit";
  const submitLabel = isSubmitBusy ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>{editOfferData ? "Resubmitting..." : "Submitting..."}</span>
    </span>
  ) : (
    defaultLabel
  );

  return (
    <div className="w-full flex flex-col items-center justify-center mt-28 sm:mt-20">
      <div className="sm:w-[100%] md:w-[95%] lg:w-[90%] xl:w-[85%] offerform">
        <h2 className="text-[26px] sm:text-[32px] font-[600] text-[#00ADEF] font-poppins text-center mb-4 sm:mb-1">
          Review and Approve <span className="text-[#303030]">Your Offer</span>
        </h2>
        <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676] mb-10 text-center">
          You're almost done! Check your details and make sure everything is accurate.
        </p>
        <MobileViews offerData={offerData} merchantData={merchantData} selectedMerchant={selectedMerchant} />
      </div>

      {/* Show confetti only for first offer */}
      {showCelebration && <Confetti />}

      {/* Always show success modal for all offers */}
      {showSuccessModal && (
        <div
          className="offer-success-modal-backdrop"
          onClick={handleCloseCelebration}
        >
          <div
            className="offer-success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Offer Submitted!</h3>
            <p className="offer-success-message">
              Your offer has been sent to admin. Once the offer has been
              approved you will receive an email notification.
            </p>
            <div className="offer-success-actions">
              <PrimaryButton
                className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                label="OK"
                onClick={handleCloseCelebration}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferReview;
